import { getPeopleOrPerson } from "utils/helperFunctions";
import { REFLECTION_STATUS_MAP } from "./constants";
import {
  ChildGoal,
  FetchGoalsParams,
  GoalItem,
  GoalReflection,
  ReflectionStatus,
} from "./types";

export const getCapitalizeWord = (word?: string) => {
  if (!word) return "";
  return word.charAt(0).toUpperCase() + word.slice(1);
};

export const getStatusLabel = (status: ReflectionStatus) =>
  REFLECTION_STATUS_MAP[status].label;

export const getStatusBG = (status: ReflectionStatus) =>
  REFLECTION_STATUS_MAP[status].bg;

export const getStatusText = (status: ReflectionStatus) =>
  REFLECTION_STATUS_MAP[status].text;

// (Optional but useful)
export const getStatusMeta = (status: ReflectionStatus) =>
  REFLECTION_STATUS_MAP[status];

export const getGoalsParamKey = (key: FetchGoalsParams) => {
  const { refresh, ...rawParams } = key;
  return Object.entries(rawParams).reduce((acc, [key, value]) => {
    if (!value) return acc;
    if (acc) acc += "&";
    return acc + `${key}=${value}`;
  }, "");
};

const getFilterParam = (
  urlParams: URLSearchParams,
  filter: string
): URLSearchParams => {
  // create copy of urlParams
  const params = new URLSearchParams(urlParams);
  switch (filter) {
    // "all" | "active" | "past"
    case "all":
    case "active":
    case "past":
      params.append("filter", filter);
      break;
    // | "on_track" | "some_challenges" | "unlikely_to_reach"
    case "on_track":
    case "some_challenges":
    case "unlikely_to_reach":
      params.append("reflectionStatus", filter);
      break;
    default:
      params.append("type", filter);
      break;
  }

  return params;
};

export const buildGoalsQueryParams = (
  params: FetchGoalsParams
): URLSearchParams => {
  const {
    viewUserId,
    page,
    filter = "all",
    is_org_goal,
    departmentId,
    reflectionStatus,
    visibility,
    search,
    isAllTab,
    perPage,
    archived,
  } = params;

  const urlParams = new URLSearchParams();

  if (!is_org_goal && viewUserId) {
    urlParams.append("viewUserId", String(viewUserId));
  }
  if (page) {
    urlParams.append("page", String(page));
  }
  if (departmentId) {
    urlParams.append("departmentId", departmentId);
  }
  if (reflectionStatus) {
    urlParams.append("reflectionStatus", reflectionStatus);
  }
  if (visibility) {
    urlParams.append("visibility", visibility);
  }
  if (search) {
    urlParams.append("search", search);
  }
  if (typeof isAllTab === "boolean") {
    urlParams.append("isAllTab", String(isAllTab));
  }
  if (typeof is_org_goal === "boolean") {
    urlParams.append("is_org_goal", String(is_org_goal));
  }
  if (typeof perPage === "number") {
    urlParams.append("perPage", String(perPage));
  }
  urlParams.append("archived", archived ? "1" : "0");

  return getFilterParam(urlParams, filter);
};

export const formatGoalStatus = (status: string) => {
  switch (status) {
    case "on_track":
      return "On track";
    case "some_challenges":
      return "Some challenges";
    case "unlikely_to_reach":
      return "Unlikely to reach";
    default:
      return "";
  }
};

export const getLatestReflection = (goal: GoalItem | ChildGoal) => {
  if (!Array.isArray(goal.reflections) || goal.reflections.length === 0) {
    return null;
  }

  return [...goal.reflections]
    .sort(
      (a: GoalReflection, b: GoalReflection) =>
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    )
    .at(-1);
};

export const getGoalStatus = (goal: GoalItem): ReflectionStatus | null => {
  const reflection = getLatestReflection(goal);
  return reflection?.status ?? null;
};

export const getGoalProgress = (goal: GoalItem): number | null => {
  if (!Array.isArray(goal.reflections) || goal.reflections.length === 0) {
    return null;
  }

  const validProgresses = goal.reflections
    .map((r: GoalReflection) => r.progressPercent)
    .filter((p: number): p is number => typeof p === "number");

  if (validProgresses.length === 0) {
    return null;
  }

  const avg = Math.round(
    validProgresses.reduce((sum: number, p: number) => sum + p, 0) /
      validProgresses.length
  );

  return avg;
};

export const getCreatedAtDate = (goalDate: string | Date) => {
  const date = new Date(goalDate);

  return date.toLocaleDateString("en-US", {
    month: "short", // Jan, Feb, Mar
    day: "numeric",
    year: "numeric",
  });
};

export const getVisibilityToText = (goal: GoalItem, isSelfGoal: boolean) => {
  let textStem = "Your reflection";
  if (!isSelfGoal) {
    textStem = "This reflection";
  }

  switch (goal.visibility) {
    case "private": {
      return `${textStem} will only be visible to you.`;
    }
    case "custom": {
      let totalCustomUsers = goal.customAllowedUserIdsAll?.length ?? 0;
      if (!isSelfGoal) {
        totalCustomUsers -= 1; // If not viewing self then that user is included in the custom list so we need to remove count by 1
      }
      return `${textStem} will only be visible to you and ${totalCustomUsers} ${getPeopleOrPerson(
        totalCustomUsers
      )}`;
    }
    case "shared": {
      return `${textStem} will be visible to everyone in your organization.`;
    }
    case "standard":
    default: {
      if (!isSelfGoal) {
        return `${textStem} will be visible to this person, their manager, and admins.`;
      }
      return `${textStem} will only be visible to you, your manager, and admins.`;
    }
  }
};
